import { listCountsHook } from "_shared/_api/list-counts/list-counts-hook";
import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import { api } from "_shared/_stores/api";
import type { RootState } from "_shared/_stores/root-reducer";
import type { AppDispatch } from "_shared/_stores/store-types";
import Guid from "_shared/guids/guid";
import z from "zod";
import { AccountType } from "users/account-types/account-type";
import TeamTemplateAPIRequest from "work/team-templates/_api/commands/team-template-api-request";
import UpdateTeamTemplateAPIRequest from "work/team-templates/_api/commands/update-team-template-api-request";
import WorkTeamTemplateQuery from "work/team-templates/_api/queries/team-template-query";
import TeamTemplateSchema, { TeamTemplateJSON } from "work/team-templates/_schema/team-template-schema";
import TeamTemplate from "work/team-templates/team-template";
import { TeamTemplateParty } from "work/team-templates/team-template-party";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import VendorTeamName from "work/work-agreements/teams/vendor-team/vendor-team-name/vendor-team-name";

const teamTemplateListTag = { type: "TeamTemplate" as const, id: "LIST" };

const count = z.number().int().nonnegative();

/**
 * The Templates tab's badge and its Archived chip's count, from `GET work/team-templates/counts`
 * (#6709): the templates that are not archived, and the archived ones. Each takes the list's own criteria.
 */
export const TeamTemplateListCountsJSONZ = z.object({
  templates: count,
  archived: count,
});

export type TeamTemplateListCounts = z.infer<typeof TeamTemplateListCountsJSONZ>;

type TeamTemplateApiCacheContext = {
  dispatch: AppDispatch;
  getState: () => RootState;
};

type TeamTemplateUpsertCacheContext = TeamTemplateApiCacheContext & {
  teamTemplateJSON: TeamTemplateJSON;
};

type TeamTemplateRemoveCacheContext = TeamTemplateApiCacheContext & {
  templateIdValue: string;
};

const invalidateTeamTemplateListApiCaches = (dispatch: AppDispatch) => {
  dispatch(teamTemplatesApi.util.invalidateTags([teamTemplateListTag]));
};

export const upsertTeamTemplateApiCaches = (context: TeamTemplateUpsertCacheContext) => {
  const { dispatch, teamTemplateJSON } = context;
  if (!teamTemplateJSON.id) {
    throw new Error("Cannot cache team template without an id.");
  }

  dispatch(
    teamTemplatesApi.util.upsertQueryEntries([
      {
        endpointName: "getTeamTemplateById",
        arg: { templateId: teamTemplateJSON.id },
        value: teamTemplateJSON,
      },
    ]),
  );
  invalidateTeamTemplateListApiCaches(dispatch);
};

export const removeTeamTemplateApiCaches = (context: TeamTemplateRemoveCacheContext) => {
  const { dispatch, templateIdValue } = context;

  dispatch(
    teamTemplatesApi.util.upsertQueryEntries([
      {
        endpointName: "getTeamTemplateById",
        arg: { templateId: templateIdValue },
        value: null,
      },
    ]),
  );
  invalidateTeamTemplateListApiCaches(dispatch);
};

export const teamTemplatesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTeamTemplates: build.query<Paginated<TeamTemplateJSON>, { query: WorkTeamTemplateQuery; interfaceName?: string }>({
      query: ({ query }) => ({
        url: "work/team-templates",
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(TeamTemplateSchema.TeamTemplateJSONZ),
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          const { data: teamTemplatesResponse } = await queryFulfilled;
          const entries = teamTemplatesResponse.data
            .filter((teamTemplateJSON): teamTemplateJSON is TeamTemplateJSON & { id: string } =>
              Boolean(teamTemplateJSON.id)
            )
            .map((teamTemplateJSON) => ({
              endpointName: "getTeamTemplateById" as const,
              arg: { templateId: teamTemplateJSON.id },
              value: teamTemplateJSON,
            }));
          if (entries.length > 0) {
            dispatch(teamTemplatesApi.util.upsertQueryEntries(entries));
          }
        } catch {
          // Failed list requests have no rows to hydrate into the per-template cache.
        }
      },
      transformResponse: (response: TeamTemplateJSON[], meta): Paginated<TeamTemplateJSON> => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(headers),
        };
      },
      providesTags: (result) =>
        result
          ? [
            ...result.data.map((template) => ({
              type: "TeamTemplate" as const,
              id: template.id ?? undefined,
            })),
            teamTemplateListTag,
          ]
          : [teamTemplateListTag],
    }),

    getTeamTemplateListCounts: build.query<TeamTemplateListCounts, { criteria: string }>({
      query: ({ criteria }) => `work/team-templates/counts${criteria ? `?${criteria}` : ""}`,
      rawResponseSchema: TeamTemplateListCountsJSONZ,
      onSchemaFailure: logSchemaFailure,
      // Every save, archive and delete invalidates the list, and the counts are the list's.
      providesTags: [teamTemplateListTag],
    }),

    getTeamTemplateById: build.query<TeamTemplateJSON | null, { templateId: string }>({
      query: ({ templateId }) => `work/team-templates/${templateId}`,
      rawResponseSchema: TeamTemplateSchema.TeamTemplateJSONZ.nullable(),
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { templateId }) => [{ type: "TeamTemplate" as const, id: templateId }],
    }),

    createTeamTemplate: build.mutation<
      TeamTemplateJSON,
      {
        entityId: string;
        team: VendorTeam;
        teamName: VendorTeamName;
        creatorType: AccountType;
        /** Which side's roster is being saved. Omitted means the vendor side, as it always did. */
        party?: TeamTemplateParty;
      }
    >({
      query: ({ entityId, team, teamName, creatorType, party }) => ({
        url: "work/team-templates",
        method: "POST",
        body: {
          ...new TeamTemplateAPIRequest(
            new Guid(entityId),
            team,
            teamName,
            creatorType,
            undefined,
            party ?? TeamTemplateParty.Vendor,
          ),
        },
      }),
      rawResponseSchema: TeamTemplateSchema.TeamTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: teamTemplateJSON } = await queryFulfilled;
          upsertTeamTemplateApiCaches({
            dispatch: dispatch as AppDispatch,
            getState: getState as () => RootState,
            teamTemplateJSON,
          });
        } catch {
          // Failed mutations should leave local template caches untouched.
        }
      },
    }),

    updateTeamTemplate: build.mutation<
      TeamTemplateJSON,
      { templateId: string; originalTemplate: TeamTemplate; updatedTeam: VendorTeam; updatedTeamName: VendorTeamName }
    >({
      query: ({ templateId, originalTemplate, updatedTeam, updatedTeamName }) => ({
        url: `work/team-templates/${templateId}`,
        method: "PATCH",
        body: new UpdateTeamTemplateAPIRequest(originalTemplate, updatedTeam, updatedTeamName).payload,
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: TeamTemplateSchema.TeamTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: teamTemplateJSON } = await queryFulfilled;
          upsertTeamTemplateApiCaches({
            dispatch: dispatch as AppDispatch,
            getState: getState as () => RootState,
            teamTemplateJSON,
          });
        } catch {
          // Failed mutations should leave local template caches untouched.
        }
      },
    }),

    deleteTeamTemplate: build.mutation<void, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `work/team-templates/${templateId}`,
        method: "DELETE",
      }),
      onQueryStarted: async ({ templateId }, { dispatch, getState, queryFulfilled }) => {
        try {
          await queryFulfilled;
          removeTeamTemplateApiCaches({
            dispatch: dispatch as AppDispatch,
            getState: getState as () => RootState,
            templateIdValue: templateId,
          });
        } catch {
          // Failed mutations should leave local template caches untouched.
        }
      },
    }),

    archiveTeamTemplate: build.mutation<TeamTemplateJSON, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `work/team-templates/${templateId}/archive`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: TeamTemplateSchema.TeamTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: teamTemplateJSON } = await queryFulfilled;
          upsertTeamTemplateApiCaches({
            dispatch: dispatch as AppDispatch,
            getState: getState as () => RootState,
            teamTemplateJSON,
          });
        } catch {
          // Failed mutations should leave local template caches untouched.
        }
      },
    }),

    unarchiveTeamTemplate: build.mutation<TeamTemplateJSON, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `work/team-templates/${templateId}/unarchive`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: TeamTemplateSchema.TeamTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: teamTemplateJSON } = await queryFulfilled;
          upsertTeamTemplateApiCaches({
            dispatch: dispatch as AppDispatch,
            getState: getState as () => RootState,
            teamTemplateJSON,
          });
        } catch {
          // Failed mutations should leave local template caches untouched.
        }
      },
    }),

    registerTeamTemplateToMarketplace: build.mutation<TeamTemplateJSON, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `work/team-templates/${templateId}/register-to-marketplace`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: TeamTemplateSchema.TeamTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: teamTemplateJSON } = await queryFulfilled;
          upsertTeamTemplateApiCaches({
            dispatch: dispatch as AppDispatch,
            getState: getState as () => RootState,
            teamTemplateJSON,
          });
        } catch {
          // Failed mutations should leave local template caches untouched.
        }
      },
    }),
  }),
});

export const {
  useGetTeamTemplatesQuery,
  useGetTeamTemplateByIdQuery,
  useCreateTeamTemplateMutation,
  useUpdateTeamTemplateMutation,
  useDeleteTeamTemplateMutation,
  useArchiveTeamTemplateMutation,
  useUnarchiveTeamTemplateMutation,
  useRegisterTeamTemplateToMarketplaceMutation,
  useGetTeamTemplateListCountsQuery,
} = teamTemplatesApi;

export const useTeamTemplateListCounts = listCountsHook<{ criteria: string }, TeamTemplateListCounts>(
  useGetTeamTemplateListCountsQuery,
);
